import PageHeader from 'components/PageHeader';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FeatureFlag } from 'types/features';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import InvoicesTable, { DEFAULT_INVOICE_TAB_URL_KEY } from './InvoicesTable';
import { useDefaultInvoiceTabKey } from './util/useDefaultInvoiceTabKey';
import { useValidInvoiceTab } from './util/useValidInvoiceTab';

const InvoicesPage = (): JSX.Element => {
    const showOnDemandTotals = useIsFeatureEnabled(FeatureFlag.SHOW_INVOICE_COLUMN_TOTALS_GLOBAL_LIST);
    const isKsefTabEnabled = useIsFeatureEnabled(FeatureFlag.KSEF_INVOICES_LIST);
    const { invoiceTab } = useParams<{ invoiceTab?: string }>();
    const navigate = useNavigate();
    const defaultTabKey = useDefaultInvoiceTabKey();

    // Unknown invoiceTab values dispatch /404. A known-but-feature-gated tab (e.g. ksef
    // without the flag) silently falls back to the main tab — the URL is structurally
    // valid, the user just lacks the feature.
    const isValidTab = useValidInvoiceTab(invoiceTab);
    const isKsefHiddenForUser = invoiceTab === 'ksef' && !isKsefTabEnabled;

    useEffect(() => {
        if (invoiceTab !== undefined && isValidTab && isKsefHiddenForUser) {
            navigate(`/invoices/${DEFAULT_INVOICE_TAB_URL_KEY}`, { replace: true });
        }
    }, [invoiceTab, isValidTab, isKsefHiddenForUser, navigate]);

    if (invoiceTab === undefined) {
        return <Navigate to={`/invoices/${defaultTabKey}`} replace />;
    }

    if (!isValidTab || isKsefHiddenForUser) {
        return <></>;
    }

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <PageHeader hostsList titleId={'invoices.invoices'} showBreadcrumb />
                <InvoicesTable key={invoiceTab} showOnDemandTotals={showOnDemandTotals} pathBasedTabId={invoiceTab} />
            </div>
        </PaddedLayout>
    );
};

export default InvoicesPage;
